import { Promise as BluebirdPromise } from 'bluebird';
import { get, isEmpty, keyBy, orderBy, pick } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { RuntimeContext } from '../../../../common/RuntimeContext';
import {
    clearSource,
    getActiveUserWelcomeAsset,
    getManyCollections,
    getManyUserCollections,
    getManyUserLearningAssets,
    getRecommendedAndAssignedCollections,
    postPeerRecommendations,
    postUserCollection,
    putUserCollectionContent
} from '../../../shared/actions/index';
import RecommendationModal from '../../../shared/components/modals/RecommendationModal';
import Page from '../../../shared/components/Page';
import VerbText from '../../../shared/components/VerbText';
import { DEFAULT_CUSTOM_DISCOVER_PAGE, LICENSE_TYPES, SEGMENT_EVENTS } from '../../../shared/Constants';
import CollectionSubscriptionBanner from '../../../shared/containers/Collection/CollectionSubscriptionBanner';
import {
    makeFilterByDictionary,
    makeGetFilteredObjectsFromState,
    makeGetUserCollectionByUser,
    makeGetUserCollectionContent
} from '../../../shared/selectors/index';
import { getCompletionStatusForUserCollection } from '../../../shared/utils/UserLearningAssetUtils';
import BannerUserCollectionWidget from '../../components/widgets/BannerUserCollection/BannerUserCollectionWidget';
import CustomDiscoverRow from './CustomDiscoverRow';
import DimensionsDiscoverRow from './DimensionsDiscoverRow';
import NewlyReleasedDiscoverRow from './NewlyReleasedDiscoverRow';
import TeamsDiscoverRow from './TeamsDiscoverRow';
import UserCollectionsDiscoverRow from './UserCollectionsDiscoverRow';

export class CustomDiscover extends Component {
    static propTypes = {
        getManyCollections: PropTypes.func.isRequired,
        getManyUserCollections: PropTypes.func.isRequired,
        getManyUserLearningAssets: PropTypes.func.isRequired,
        putUserCollectionContent: PropTypes.func,
        clearSource: PropTypes.func,
        postUserCollection: PropTypes.func,
        isAccountOwner: PropTypes.bool,
        isAuthenticated: PropTypes.bool,
        isVerbAdmin: PropTypes.bool,
        loggedInUser: PropTypes.object,
        history: PropTypes.object,
        userCompany: PropTypes.object,
        collectionsById: PropTypes.object,
        userCollectionsByCollectionId: PropTypes.object,
        userCollections: PropTypes.array,
        collectionLoading: PropTypes.bool,
        userCollectionLoading: PropTypes.bool,
        userLearningAssetLoading: PropTypes.bool,
        unsubbedCollections: PropTypes.array,
        frameworkDimensions: PropTypes.array,
        userTeams: PropTypes.array,
        source: PropTypes.object,
        getRecommendedAndAssignedCollections: PropTypes.func,
        recommendedCollections: PropTypes.array,
        assignedCollections: PropTypes.array,
        teamsById: PropTypes.object,
        postPeerRecommendations: PropTypes.func,
        getActiveUserWelcomeAsset: PropTypes.func
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);

        this.state = {
            isWelcome: false,
            loading: false,
            closeWidget: false,
            source: null,
            modalOpen: false
        };
    }

    _handleHomeDataResults = () =>
        this.setState({
            loading: false
        });

    // eslint-disable-next-line camelcase
    componentDidMount() {
        const {
            userCollections = [],
            recommendedCollections = [],
            assignedCollections = [],
            loggedInUser
        } = this.props;
        const welcome = get(this.props, 'match.params.welcome', '');

        this.setState({ loading: true, isWelcome: !!welcome, source: this.props.source || {} });
        if (userCollections.length && (recommendedCollections.length || assignedCollections.length)) {
            return this._handleHomeDataResults();
        }

        return this._getHomeData().then(this._handleHomeDataResults);
    }

    componentDidUpdate(prevProps) {
        const {
            loggedInUser,
            getManyUserLearningAssets,
            getManyUserCollections,
            getRecommendedAndAssignedCollections,
            getActiveUserWelcomeAsset
        } = this.props;
        const promises = [];
        if ((!prevProps.loggedInUser || !prevProps.loggedInUser._id) && loggedInUser && loggedInUser._id) {
            promises.push(
                getManyUserLearningAssets({ userProfileId: loggedInUser._id, pathsContext: this.context.pathsWeb })
            );
            promises.push(
                getManyUserCollections({ userProfileId: loggedInUser._id, pathsContext: this.context.pathsWeb })
            );
            promises.push(
                getActiveUserWelcomeAsset({ userProfileId: loggedInUser._id, pathsContext: this.context.pathsWeb })
            );
        }

        if (promises.length) {
            return BluebirdPromise.all(promises).then(
                getRecommendedAndAssignedCollections({
                    userProfileId: loggedInUser._id,
                    pathsContext: this.context.pathsWeb
                })
            );
        }
        return null;
    }

    _getHomeData = () => {
        const {
            getManyCollections,
            getManyUserLearningAssets,
            getManyUserCollections,
            loggedInUser,
            source = {},
            clearSource,
            getRecommendedAndAssignedCollections,
            getActiveUserWelcomeAsset
        } = this.props;
        const promises = [getManyCollections({ pathsContext: this.context.pathsWeb, isActive: true })];
        if (!isEmpty(loggedInUser)) {
            promises.push(
                getManyUserLearningAssets({ userProfileId: loggedInUser._id, pathsContext: this.context.pathsWeb })
            );
            promises.push(
                getManyUserCollections({ userProfileId: loggedInUser._id, pathsContext: this.context.pathsWeb })
            );
            promises.push(
                getRecommendedAndAssignedCollections({
                    userProfileId: loggedInUser._id,
                    pathsContext: this.context.pathsWeb
                })
            );

            promises.push(
                getActiveUserWelcomeAsset({ userProfileId: loggedInUser._id, pathsContext: this.context.pathsWeb })
            );
        }

        if (!isEmpty(source)) {
            promises.push(clearSource());
        }
        return BluebirdPromise.all(promises);
    };

    handleDelayedCreateLearningAssetsForUserCollection = (userCollectionId) => {
        const { putUserCollectionContent, history } = this.props;

        putUserCollectionContent({ userCollectionId, pathsContext: this.context.pathsWeb }).then((userCollection) =>
            history.push({
                pathname: `/workspace/${userCollection.content[0].userLearningAssetId}`,
                state: {
                    userCollection: {
                        id: userCollection._id,
                        started: true
                    }
                }
            })
        );
    };

    subscribeToCollection = (collection) => () => {
        const { postUserCollection, history } = this.props;
        const newUserCollection = {};
        newUserCollection.collectionId = collection._id;

        return postUserCollection({ body: newUserCollection, pathsContext: this.context.pathsWeb }).then(
            (createdCollection) => {
                history.push(`/collection/${createdCollection.collectionId}`, { showBanner: true });
            }
        );
    };

    _renderUserCollectionsSection = (userCollections = [], recommendedCollections) => {
        const { history, loggedInUser = {} } = this.props;
        let userCollectionToPass;
        const orderedUserCollections = orderBy(userCollections, ['lastUpdated'], ['desc']);
        const userCollectionDict = userCollections.reduce((acc, uc) => {
            acc[uc.collectionId] = uc;
            return acc;
        }, {});

        const currentUserCollection =
            loggedInUser.currentUserCollection &&
            loggedInUser.currentUserCollection.currentCollectionId &&
            userCollectionDict[loggedInUser.currentUserCollection.currentCollectionId];

        if (currentUserCollection && currentUserCollection.percentComplete < 100) {
            userCollectionToPass = currentUserCollection;
        } else if (orderedUserCollections.length > 1) {
            [userCollectionToPass] = orderedUserCollections.filter(
                (userCollection) => userCollection.percentComplete < 100
            );
            if (!userCollectionToPass) {
                [userCollectionToPass] = orderedUserCollections;
            }
        } else {
            [userCollectionToPass] = orderedUserCollections;
        }

        if (userCollectionToPass && userCollectionToPass.percentComplete === 100) {
            let selectedCollection;
            for (let i = 0; i < recommendedCollections.length; i += 1) {
                selectedCollection = recommendedCollections[i];
                if (selectedCollection && !userCollectionDict[selectedCollection._id]) {
                    return (
                        <CollectionSubscriptionBanner
                            collection={selectedCollection}
                            history={history}
                            createUserCollection={this.subscribeToCollection(selectedCollection)}
                        />
                    );
                }
            }
        }
        return (
            <BannerUserCollectionWidget
                isHome={true}
                handleDelayedCreateLearningAssetsForUserCollection={
                    this.handleDelayedCreateLearningAssetsForUserCollection
                }
                userCollection={userCollectionToPass}
                className={'user-collections-widget'}
            />
        );
    };

    _shouldHideLearningStream = () => {
        const { isAuthenticated, userCollections, userCollectionLoading, userLearningAssetLoading } = this.props;
        if (!isAuthenticated) {
            return true;
        }
        if (isEmpty(userCollections) && (!userCollectionLoading || !userLearningAssetLoading)) {
            return true;
        }
        return false;
    };

    handleRecommendationModalOpen = () => {
        const { modalOpen } = this.state;

        this.setState({
            modalOpen: !modalOpen
        });
    };

    onCollectionCardClick = (collection) => {
        this.setState({
            collectionIdToRecommend: collection.collectionId || collection._id
        });
    };

    /**
     * @param categories
     * @returns {*}
     * @private
     */
    _createCategoryRows = (categories) => {
        const {
            collectionsById,
            userCollections = [],
            userCollectionsByCollectionId,
            collectionLoading,
            unsubbedCollections,
            userCompany,
            frameworkDimensions,
            recommendedCollections,
            assignedCollections,
            isVerbAdmin,
            userTeams
        } = this.props;
        const inProgressCollections = userCollections.filter(
            (userCollection) =>
                userCollection.status === 'IN_PROGRESS' &&
                userCollection.percentComplete < 100 &&
                userCollection.type !== 'COLLAB' &&
                (!userCollection.recommendationInfo ||
                    (userCollection.recommendationInfo &&
                        userCollection.recommendationInfo.code !== 'ASSIGNED' &&
                        userCollection.recommendationInfo.code !== 'RECOMMENDED'))
        );

        const completedUserCollections = userCollections.filter(
            (userCollection) => userCollection.status === 'COMPLETED' || userCollection.percentComplete === 100
        );

        return categories.map((category, index) => {
            if (!isEmpty(userCompany)) {
                if (category.type === 'COLLECTION' 
                    || category.type === 'GLOBAL_CONTENT_ROW'
                    || category.type === 'IMPROVE_YOUR_COMMUNICATION'
                    || category.type === 'THINK_LIKE_A_LEADER'
                    || category.type === 'ESSENTIAL_MANAGER_SKILLS'
                    || category.type === 'POPULAR_ON_PATHS') {
                    return (
                        <CustomDiscoverRow
                            isCollectionsOrderLocked={category.isOrderLocked}
                            handleRecommendationModalOpen={this.handleRecommendationModalOpen}
                            onCardClick={this.onCollectionCardClick}
                            currentCategory={category}
                            collectionsById={collectionsById}
                            userCollections={userCollections}
                            userCollectionsByCollectionId={userCollectionsByCollectionId}
                            collectionLoading={collectionLoading}
                            userCompany={userCompany}
                            index={index}
                            categories={categories}
                            key={`${category.type}-${index}`}
                        />
                    );
                }
                if (category.type === 'RECOMMENDED_COLLECTIONS') {
                    return (
                        <CustomDiscoverRow
                            handleRecommendationModalOpen={this.handleRecommendationModalOpen}
                            onCardClick={this.onCollectionCardClick}
                            currentCategory={category}
                            collectionsById={collectionsById}
                            userCollections={userCollections}
                            userCollectionsByCollectionId={userCollectionsByCollectionId}
                            collectionLoading={collectionLoading}
                            userCompany={userCompany}
                            index={index}
                            key={`${category.type}-${index}`}
                            recommendedCollections={recommendedCollections ?? []}
                            assignedCollections={assignedCollections ?? []}
                        />
                    );
                }
                // if (category.type === 'PRESET_JUST_RELEASED') {
                //     return (
                //         <NewlyReleasedDiscoverRow
                //             handleRecommendationModalOpen={this.handleRecommendationModalOpen}
                //             onCardClick={this.onCollectionCardClick}
                //             unsubbedCollections={unsubbedCollections}
                //             currentCategory={category}
                //             collectionLoading={collectionLoading}
                //             userCompany={userCompany}
                //             index={index}
                //             key={`${category.type}-${index}`}
                //         />
                //     );
                // }
                if (category.type === 'PRESET_DIMENSIONS_SECTION') {
                    return (
                        <DimensionsDiscoverRow
                            dimensions={frameworkDimensions}
                            index={index}
                            key={`${category.type}-${index}`}
                            userCompany={userCompany}
                            currentCategory={category}
                        />
                    );
                }
                if (category.type === 'IN_PROGRESS_SECTION') {
                    return inProgressCollections && inProgressCollections.length ? (
                        <UserCollectionsDiscoverRow
                            handleRecommendationModalOpen={this.handleRecommendationModalOpen}
                            onCardClick={this.onCollectionCardClick}
                            userCollections={inProgressCollections}
                            loading={this.state.loading}
                            headingMessageId={'HEADING_IN_PROGRESS'}
                            defaultHeadingMessage={'IN PROGRESS'}
                            subTextId={'HOME_COLLECTIONS_CONTINUE'}
                            defaultSubTextMessage={'Click a collection to pick up where you left off.'}
                        />
                    ) : null;
                }

                if (category.type === 'TEAMS_SECTION') {
                    return (
                        <TeamsDiscoverRow
                            userTeams={userTeams}
                            cardBackgroundColor={index % 2 === 0 ? 'eggshell' : 'white'}
                            onCardClick={(team) => {}}
                        />
                    );
                }

                if (category.type === 'COMPLETED_COLLECTIONS') {
                    let addons = [];
                    const blankCollectionCard = <div className={'blank-collection-card-content'} />;
                    if (!completedUserCollections.length && !this.state.loading) {
                        addons = [
                            <div
                                key={'completed-blank-1'}
                                className={'vertical-collection-card blank-collection-card col-12'}
                            >
                                {blankCollectionCard}
                            </div>,
                            <div
                                key={'completed-blank-2'}
                                className={'vertical-collection-card blank-collection-card col-12'}
                            >
                                {blankCollectionCard}
                            </div>,
                            <div
                                key={'completed-blank-3'}
                                className={'vertical-collection-card blank-collection-card col-12'}
                            >
                                {blankCollectionCard}
                            </div>,
                            <div
                                key={'completed-blank-4'}
                                className={'vertical-collection-card blank-collection-card col-12'}
                            >
                                {blankCollectionCard}
                            </div>
                        ];
                    }

                    return (
                        <UserCollectionsDiscoverRow
                            handleRecommendationModalOpen={this.handleRecommendationModalOpen}
                            onCardClick={this.onCollectionCardClick}
                            userCollections={completedUserCollections}
                            loading={this.state.loading}
                            headingMessageId={'COMPLETED_COLLECTIONS'}
                            defaultHeadingMessage={'Completed'}
                            key={`${category.type}-${index}`}
                            addons={addons}
                        />
                    );
                }
                return null;
            }
            return null;
        });
    };

    _renderInviteBanner() {
        const { userTeams = [], userCompany, isVerbAdmin } = this.props;
        const { source = {} } = this.state;
        const teamsByPermalink = userTeams.reduce((acc, team) => {
            if (team.permalink) {
                return { ...acc, [team.permalink]: team };
            }
            return acc;
        }, {});
        if (teamsByPermalink[source.teamPermalink]) {
            const team = teamsByPermalink[source.teamPermalink];
            const link = <Link to={`/teams/${team._id}`}>{'Go to my team >'}</Link>;
            return (
                <div className={'row invite-banner'}>
                    <div
                        className={'banner-container container'}
                        onClick={() => {}}
                    >
                        <VerbText
                            className={'banner-message'}
                            messageId={'HOME_INVITE_BANNER'}
                            defaultMessage={`You've been invited to a new team! Collaborate and learn with your team members on the ${team.name} page. ${link}`}
                            valuesToInject={{ link, name: team.name || '' }}
                        />
                    </div>
                </div>
            );
        }

        return null;
    }

    render() {
        const {
            isAccountOwner,
            isVerbAdmin,
            collectionsById = {},
            userCompany,
            userCompany: { customDiscover = {} },
            teamsById,
            loggedInUser,
            postPeerRecommendations
        } = this.props;
        const { modalOpen, collectionIdToRecommend, source = {} } = this.state;
        const recommendedCollections = [];
        if (customDiscover.categories && !isEmpty(collectionsById)) {
            customDiscover.categories.forEach((category) => {
                category.content.forEach((collectionId) => recommendedCollections.push(collectionsById[collectionId]));
            });
        }

        const shouldHideLearningStream = this._shouldHideLearningStream();
        return (
            <Page className={'home-wrapper container-fluid custom-discover'}>
                {source && source.teamPermalink ? this._renderInviteBanner() : null}
                {this._createCategoryRows(
                    !isEmpty(customDiscover?.categories)
                        ? customDiscover.categories
                        : DEFAULT_CUSTOM_DISCOVER_PAGE.categories,
                    shouldHideLearningStream
                )}
                <RecommendationModal
                    isOpen={modalOpen}
                    onClose={this.handleRecommendationModalOpen}
                    parentId={collectionIdToRecommend}
                    onSend={postPeerRecommendations}
                    companyId={loggedInUser.companyId}
                    company={userCompany}
                    loggedInUser={loggedInUser}
                    currentUserEmail={loggedInUser.email}
                    parentType={'COLLECTION'}
                    teamsById={teamsById}
                    parentEntity={collectionsById[collectionIdToRecommend] || {}}
                    isAccountOwner={isAccountOwner}
                    isVerbAdmin={isVerbAdmin}
                />
            </Page>
        );
    }
}

export const makeMapStateToProps = () => {
    const getUserCollectionsObjectsFromStateById = makeGetUserCollectionByUser();
    const getUserCollectionContent = makeGetUserCollectionContent();
    const getFilteredCollectionsFromState = makeGetFilteredObjectsFromState('collections');
    const getUnsubbedCollections = makeFilterByDictionary('_id');
    const getFrameworks = makeGetFilteredObjectsFromState('learningFrameworks');
    const getUserActiveTeams = makeGetFilteredObjectsFromState('teams');

    const derivedMapStateToProps = (state) => {
        const {
            auth: { isAuthenticated, isAccountOwner, isVerbAdmin, loggedInUser = {}, source = {} },
            companies,
            collections,
            userCollections,
            userLearningAssets,
            dimensions,
            learningFrameworks,
            teams,
            userWelcomeAssets: { activeUserWelcomeAsset }
        } = state;
        const userCompany = (companies && companies.byId && companies.byId[loggedInUser.companyId]) || {};
        let userCollectionContent;
        let userCollectionCompletenessData;
        const userCollectionsArray = getUserCollectionsObjectsFromStateById(state, loggedInUser._id);
        const userCollectionsWithCompletenessData = [];
        userCollectionsArray.forEach((userCollection) => {
            if (
                collections.byId[userCollection.collectionId] &&
                collections.byId[userCollection.collectionId].isActive
            ) {
                userCollectionContent = getUserCollectionContent(userCollection, state);
                userCollectionCompletenessData = getCompletionStatusForUserCollection(userCollectionContent);
                userCollectionsWithCompletenessData.push({
                    ...userCollection,
                    ...userCollectionCompletenessData
                });
            }
        });

        const { hiddenCollections = [], collectionLicenses = [] } = userCompany;

        const licensesByCollectionId = keyBy(collectionLicenses, 'collectionId');
        const hiddenCollectionsById = keyBy(hiddenCollections, 'id');
        const viewableCollections = getFilteredCollectionsFromState(state, (collection) => {
            const licenseType = collection.subscriptionType || '';
            if (
                (licenseType === LICENSE_TYPES.EXCLUSIVE_COMPANY_LICENSE && !licensesByCollectionId[collection._id]) ||
                hiddenCollectionsById[collection._id]
            ) {
                return false;
            }

            return !!collection.isActive && !collection.isLocked && collection.type !== 'COLLAB';
        });

        const userCollectionsByCollectionId = keyBy(userCollectionsWithCompletenessData, 'collectionId');

        const { recommendedIds = [], assignedIds = [], byId } = collections;
        const reduceCollections = (arr, coll) => {
            if (byId[coll] && byId[coll].isActive) {
                const collection = userCollectionsByCollectionId[coll._id] ?? byId[coll._id];
                Object.assign(collection, collection, {
                    ...pick(coll, 'isRecommended', 'isAssigned', 'recommendationInfo'),
                    recommendationCount: coll.recommendationCount
                });

                arr.push(collection);
            }
            return arr;
        };

        const recommendedCollections = recommendedIds.reduce(reduceCollections, []);
        const assignedCollections = assignedIds.reduce(reduceCollections, []);

        // Learning framework
        let learningFramework;
        if (
            userCompany &&
            userCompany.hasCustomDimensions &&
            userCompany.frameworkId &&
            learningFrameworks.byId[userCompany.frameworkId]
        ) {
            learningFramework = learningFrameworks.byId[userCompany.frameworkId];
        } else {
            const [defaultFramework] = getFrameworks(state, (framework) => !!framework.isDefault);
            if (defaultFramework) {
                learningFramework = defaultFramework;
            }
        }

        let frameworkDimensions = [];

        if (learningFramework && learningFramework.dimensionIds) {
            learningFramework.dimensionIds.forEach((id) => {
                const dimension = dimensions.byId[id];
                if (dimension) {
                    if (learningFramework.primaryDimensionId && id === learningFramework.primaryDimensionId) {
                        dimension.isPrimary = true;
                    }
                    frameworkDimensions.push(dimension);
                }
            });
        } else {
            frameworkDimensions = Object.values(dimensions.byId);
        }

        return {
            isAuthenticated,
            isAccountOwner,
            isVerbAdmin,
            loggedInUser,
            userCompany,
            unsubbedCollections: getUnsubbedCollections(viewableCollections, userCollectionsByCollectionId),
            collectionLoading: collections.loading,
            userCollectionLoading: userCollections.loading,
            userLearningAssetLoading: userLearningAssets.loading,
            userCollections: userCollectionsWithCompletenessData,
            collectionsById: collections.byId,
            userCollectionsByCollectionId,
            frameworkDimensions,
            userTeams: getUserActiveTeams(state, (team) => team.isActive),
            source,
            recommendedCollections,
            assignedCollections,
            teamsById: teams.byId,
            activeUserWelcomeAsset
        };
    };

    return derivedMapStateToProps;
};

export const mapDispatchToProps = (dispatch) => ({
    getManyCollections: (params) => dispatch(getManyCollections(params)),
    getManyUserLearningAssets: (params) => dispatch(getManyUserLearningAssets(params)),
    getManyUserCollections: (params) => dispatch(getManyUserCollections(params)),
    putUserCollectionContent: (params) => dispatch(putUserCollectionContent(params)),
    postUserCollection: (params) => dispatch(postUserCollection(params)),
    clearSource: () => dispatch(clearSource()),
    getRecommendedAndAssignedCollections: (params) => dispatch(getRecommendedAndAssignedCollections(params)),
    postPeerRecommendations: (params) => dispatch(postPeerRecommendations(params)),
    getActiveUserWelcomeAsset: (params) => dispatch(getActiveUserWelcomeAsset(params))
});

export default connect(makeMapStateToProps, mapDispatchToProps)(CustomDiscover);
